import { ref, watch, computed } from 'vue'
import { exportAccountBill, updateAccountBill, type AccountBillItem } from '@/api/billApi'
import { message } from 'ant-design-vue'
import { handleExportFile, OK } from '@/common'
import { TablePaginationConfig } from 'ant-design-vue'
import type { SorterResult } from 'ant-design-vue/es/table/interface'
import { useTableLayout } from './useTableLayout'
import { useRowSelection } from './useRowSelection'
import { useOtherReason } from './useOtherReason'
import { useEditField, type ReceivedStatus, type EditingField } from './useEditField'
import { useBatchOperation } from './useBatchOperation'

export type { ReceivedStatus, EditingField }

export type StatusMapType = {
  [key: string]: {
    text: string
    color: string
  }
}

// export type ReasonCode = '0' | '10' | '20' | '30' | '40' | '41' | '50' | '60' | '70' | '80' | '90' | '100'
export type ReasonCode = '10' | '20' | '30' | '40' | '42' | '50'

export const statusMap: StatusMapType = {
  '0': { text: '待确认', color: 'default' },
  '10': { text: '未收款', color: 'warning' },
  '20': { text: '正常收款', color: 'success' },
  '30': { text: '异常收款', color: 'error' },
}

export const reasonMap: Record<ReasonCode, string> = {
  '10': '未维护收款信息',
  '20': '封号',
  '30': '无合适收款账户',
  '40': '未及时维护收款信息，推迟下月打款',
  '42': '结算金额未满1美金',
  '50': '收款信息有误',
}

export const formatAmount = (text: string | number | null | undefined) => {
  if (text === null || text === undefined || text === '') return '-'
  const num = Number(text)
  if (isNaN(num)) return '-'
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export const formatNumberWithPercent = (text: string | number | null | undefined) => {
  if (text === null || text === undefined || text === '') return '-'
  const str = String(text)
  const idx = str.indexOf('(')
  if (idx === -1) return formatAmount(str)
  const num = Number(str.slice(0, idx))
  if (isNaN(num)) return '-'
  const formattedNum = num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `${formattedNum}(${str.slice(idx + 1)}`
}
interface UseSettlementTableProps {
  data: AccountBillItem[]
  pagination?: TablePaginationConfig
  searchParams?: {
    startMonthDate: string
    endMonthDate: string
    accountIdList: number[]
    payChnl: string
  }
}

type EmitType = {
  (event: 'pagination-change', page: number, pageSize: number): void
  (event: 'sort-change', sortField: string, ascending: boolean): void
  (event: 'update-list'): void
  (event: 'selection-change', keys: string[]): void
}

export const useSettlementTable = (props: UseSettlementTableProps, emit: EmitType) => {
  const modalVisible = ref(false)
  const isBatchOperation = ref(false)

  const { tableHeight } = useTableLayout()

  const { selectedRowKeys, selectedRows, onSelectChange } = useRowSelection(emit)

  watch(
    () => props.data,
    () => {
      selectedRowKeys.value = []
      selectedRows.value = []
    },
  )

  const {
    otherReasonModalVisible,
    currentRecord,
    originalRecord,
    fileList,
    previewVisible,
    previewImage,
    previewTitle,
    textareaRef,
    handleEditCustomReason,
    handleOtherReasonConfirm,
    handleOtherReasonCancel,
  } = useOtherReason({ emit, isBatchOperation, selectedRowKeys })

  const {
    editFieldModalVisible,
    editingField,
    editingValue,
    editingMonth,
    editFieldTitle,
    usdValue,
    usdValueUnit,
    handleEditField,
    handleEditFieldConfirm,
    handleEditFieldCancel,
    handleQueryExchangeRate,
    debouncedQueryExchangeRate,
    handleWithholdingTaxChange,
  } = useEditField({
    props,
    emit,
    isBatchOperation,
    selectedRows,
    selectedRowKeys,
    currentRecord,
    payChnl: computed(() => (props as any).searchParams?.payChnl || 'google'),
  })

  const {
    handleBatchModify,
    handleBatchUpdateStatus,
    handleBatchUpdateExpected,
    handleBatchUpdateReceived,
    handleBatchModifyActualReceiptTime,
  } = useBatchOperation({
    emit,
    selectedRowKeys,
    isBatchOperation,
    currentRecord,
    otherReasonModalVisible,
    editingField,
    editingValue,
    editFieldTitle,
    editFieldModalVisible,
  })

  const handleImportSettlement = () => {
    modalVisible.value = true
  }

  const handleExportAccount = async () => {
    try {
      if (!props.searchParams) {
        message.warn('请选择账号')
        return
      }
      handleExportFile(
        await exportAccountBill({
          startMonthDate: props.searchParams.startMonthDate,
          endMonthDate: props.searchParams.endMonthDate,
          accountIdList: props.searchParams.accountIdList,
          payChnl: props.searchParams.payChnl,
        }),
        `财务结算账单`,
        'xlsx',
      )
    } catch (error) {
      console.log('[ error ] >', error)
    }
  }

  const handleStatusChange = async (value: ReceivedStatus, record: AccountBillItem) => {
    try {
      const res = await updateAccountBill({ id: record.id, receivedStatus: value })
      if (res && res.code === OK) {
        record.receivedStatus = value
        message.success('更新成功')
        emit('update-list')
      }
    } catch (error) {
      console.log('[ error ] >', error)
    }
  }

  const handleSaveBalance = (_record: AccountBillItem, _value: number | null, _originalRow: Record<string, any>) => {}

  const changePage = (page: number, pageSize: number) => {
    emit('pagination-change', page, pageSize)
  }

  const handleTableChange = (pagination: TablePaginationConfig, _: any, sorter: SorterResult<AccountBillItem>) => {
    if (pagination.current && pagination.pageSize) {
      emit('pagination-change', pagination.current, pagination.pageSize)
    }
    if (sorter && sorter.field) {
      emit('sort-change', sorter.field as string, sorter.order === 'ascend')
    }
  }

  return {
    tableHeight,
    modalVisible,
    otherReasonModalVisible,
    currentRecord,
    originalRecord,
    textareaRef,
    editFieldModalVisible,
    editingField,
    editingValue,
    editingMonth,
    editFieldTitle,
    fileList,
    previewVisible,
    previewImage,
    previewTitle,
    usdValue,
    usdValueUnit,
    changePage,
    handleImportSettlement,
    handleExportAccount,
    handleEditCustomReason,
    handleOtherReasonConfirm,
    handleOtherReasonCancel,
    handleEditField,
    handleEditFieldConfirm,
    handleEditFieldCancel,
    handleStatusChange,
    handleTableChange,
    handleBatchModify,
    handleBatchUpdateStatus,
    handleBatchUpdateExpected,
    handleBatchUpdateReceived,
    handleBatchModifyActualReceiptTime,
    formatNumberWithPercent,
    selectedRowKeys,
    onSelectChange,
    isBatchOperation,
    handleQueryExchangeRate,
    debouncedQueryExchangeRate,
    handleWithholdingTaxChange,
    handleSaveBalance,
  }
}
